import { BrowserSessionApiClient } from "./browserSessionApiClient";
import type { AdminAccessLogRecord } from "@/types/accessLogs";

const apiClient = new BrowserSessionApiClient("/api");

export type ProjectFilePackageType = "structural" | "civil" | "other";

export type ProjectFileRecord = {
  id: string;
  projectId: number;
  projectNumber: string | null;
  projectAddress: string | null;
  projectSuburb: string | null;
  packageType: ProjectFilePackageType;
  packageLabel: string;
  revisionDate: string;
  fileName: string;
  blobPath: string;
  fileSizeBytes: number | null;
  contentType: string | null;
  uploadedBy: string | null;
  createdAt: string;
  updatedAt: string | null;
  isDeleted: boolean;
};

export type ProjectFileUploadSession = {
  id: string;
  projectLabel: string;
  fileName: string;
  blobPath: string;
  packageType: ProjectFilePackageType;
  packageLabel: string;
  revisionDate: string;
  uploadUrl: string;
  uploadUrlExpiresAt: string;
  project: {
    projectId: number;
    projectNumber: string | null;
    projectName: string | null;
    siteAddress: string | null;
    suburb: string | null;
    postCode: string | null;
  };
};

export type ProjectFileUploadResult = {
  id: string;
  fileName: string;
  blobPath: string;
  packageType: ProjectFilePackageType;
  packageLabel: string;
  revisionDate: string;
  projectLabel: string;
};

export type PublicProjectFileSummary = {
  id: string;
  fileName: string;
  fileSizeBytes: number | null;
  contentType: string | null;
  packageType: ProjectFilePackageType;
  packageLabel: string;
  revisionDate: string;
  createdAt: string | null;
};

export type PublicProjectIfcModelSummary = {
  ifcFileId: string;
  fileName: string;
  fileSizeBytes: number | null;
  versionLabel: string | null;
  versionCreatedAt: string | null;
};

export type ProjectFileSessionBootstrap = {
  projectLabel: string;
  projectNumber: string | null;
  suburb: string | null;
  expiresAt: string;
  files: PublicProjectFileSummary[];
  ifcModels: PublicProjectIfcModelSummary[];
};

export type ProjectFileAccessRequest = {
  matched: true;
  projectLabel: string;
  verificationId: string;
  expiresAt: string;
};

export type ProjectFileOtpVerify = {
  sessionToken: string;
  expiresAt: string;
  portalPath: string;
};

export type ProjectFileDownloadLink = {
  fileId: string;
  fileName: string;
  downloadUrl: string;
  expiresAt: string;
};

export type ProjectFileIfcViewerSession = {
  ifcFileId: string;
  fileName: string;
  sessionToken: string;
  expiresAt: string;
  viewerPath: string;
};

function getContentType(file: File): string {
  return file.type || "application/octet-stream";
}

async function uploadFileToBlob(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": getContentType(file),
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Blob upload failed with HTTP ${response.status}.`);
  }
}

async function readJson(response: Response) {
  return response.json().catch(() => null);
}

function publicErrorMessage(payload: any, fallback: string): string {
  return payload?.error?.message || fallback;
}

async function publicRequest<T>(path: string, init?: RequestInit, fallback = "Request failed."): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init?.headers || {}),
    },
  });
  const payload = await readJson(response);
  if (!response.ok) {
    throw new Error(publicErrorMessage(payload, fallback));
  }
  return payload as T;
}

export const projectFilesService = {
  async listFiles(projectId?: number | null): Promise<ProjectFileRecord[]> {
    const response = await apiClient.request<{ files: ProjectFileRecord[] }>("/api/project-files", {
      query: projectId ? { projectId } : undefined,
    });
    return Array.isArray(response.files) ? response.files : [];
  },

  async listAccessLogs(limit = 30): Promise<AdminAccessLogRecord[]> {
    const response = await apiClient.request<{ logs: AdminAccessLogRecord[] }>("/api/project-files/access-logs", {
      query: { limit },
    });
    return Array.isArray(response.logs) ? response.logs : [];
  },

  async createUploadSession(input: {
    projectId: number;
    fileName: string;
    fileSizeBytes: number;
    contentType: string;
    packageType: ProjectFilePackageType;
    revisionDate: string;
  }): Promise<ProjectFileUploadSession> {
    return apiClient.request<ProjectFileUploadSession>("/api/project-files/upload-session", {
      method: "POST",
      body: input,
    });
  },

  async completeUpload(id: string, input: { fileSizeBytes: number; contentType: string }): Promise<{ id: string; status: string }> {
    return apiClient.request<{ id: string; status: string }>(`/api/project-files/${encodeURIComponent(id)}/complete-upload`, {
      method: "POST",
      body: input,
    });
  },

  async uploadFileForProject(input: {
    projectId: number;
    file: File;
    packageType: ProjectFilePackageType;
    revisionDate: string;
  }): Promise<ProjectFileUploadResult> {
    const session = await this.createUploadSession({
      projectId: input.projectId,
      fileName: input.file.name,
      fileSizeBytes: input.file.size,
      contentType: getContentType(input.file),
      packageType: input.packageType,
      revisionDate: input.revisionDate,
    });

    await uploadFileToBlob(session.uploadUrl, input.file);
    await this.completeUpload(session.id, {
      fileSizeBytes: input.file.size,
      contentType: getContentType(input.file),
    });

    return {
      id: session.id,
      fileName: session.fileName,
      blobPath: session.blobPath,
      packageType: session.packageType,
      packageLabel: session.packageLabel,
      revisionDate: session.revisionDate,
      projectLabel: session.projectLabel,
    };
  },

  async deleteFile(id: string): Promise<{ id: string; status: string }> {
    return apiClient.request<{ id: string; status: string }>(`/api/project-files/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },

  async createDownloadLink(id: string): Promise<ProjectFileDownloadLink> {
    return apiClient.request<ProjectFileDownloadLink>(`/api/project-files/${encodeURIComponent(id)}/download`, {
      method: "POST",
    });
  },
};

export const publicProjectFilesService = {
  async requestAccess(input: {
    email: string;
    projectRole: string;
    marketingConsent: boolean;
    projectNumber: string;
    projectAddress: string;
    suburb: string;
  }): Promise<ProjectFileAccessRequest> {
    return publicRequest<ProjectFileAccessRequest>("/api/public/project-files/access/request", {
      method: "POST",
      body: JSON.stringify(input),
    }, "No project matched those details.");
  },

  async verifyOtp(input: { verificationId: string; email: string; otpCode: string }): Promise<ProjectFileOtpVerify> {
    return publicRequest<ProjectFileOtpVerify>("/api/public/project-files/otp/verify", {
      method: "POST",
      body: JSON.stringify(input),
    }, "The OTP could not be verified.");
  },

  async getSession(sessionToken: string): Promise<ProjectFileSessionBootstrap> {
    return publicRequest<ProjectFileSessionBootstrap>(`/api/public/project-files/session/${encodeURIComponent(sessionToken)}`, {
      method: "GET",
      cache: "no-store",
    }, "The project file session could not be opened.");
  },

  async createDownloadLink(sessionToken: string, fileId: string): Promise<ProjectFileDownloadLink> {
    return publicRequest<ProjectFileDownloadLink>(
      `/api/public/project-files/session/${encodeURIComponent(sessionToken)}/files/${encodeURIComponent(fileId)}/download`,
      { method: "POST" },
      "The download link could not be prepared."
    );
  },

  async createIfcViewerSession(sessionToken: string, ifcFileId: string): Promise<ProjectFileIfcViewerSession> {
    return publicRequest<ProjectFileIfcViewerSession>(
      `/api/public/project-files/session/${encodeURIComponent(sessionToken)}/ifc/${encodeURIComponent(ifcFileId)}/viewer-session`,
      { method: "POST" },
      "The IFC model could not be opened."
    );
  },
};
